import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  message,
  Typography,
} from "antd";
import { lecheApi } from "../services/lecheApi";

const { Title, Text } = Typography;

const dateFormat = "YYYY-MM-DD";

function normalizeErrorMessage(err) {
  // backend devuelve { message: "..." } en 409
  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    err?.message ||
    "Ocurrió un error.";
  return msg;
}

export default function FrmLecheHistorial() {
  const { id } = useParams(); // animalId
  const animalId = Number(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);

  const [data, setData] = useState([]);
  const [resumen, setResumen] = useState({ promedio7Dias: 0, promedio30Dias: 0 });

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // registro actual (si edita)

  const [form] = Form.useForm();

  const [filtroRango, setFiltroRango] = useState(null); // [dayjs, dayjs] o null

  const tituloModal = editing ? "Editar registro de leche" : "Nuevo registro de leche";

  const loadListado = async () => {
    setLoading(true);
    try {
      const list = await lecheApi.listarPorAnimal(animalId);
      setData(Array.isArray(list) ? list : []);
    } catch (err) {
      message.error(normalizeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadResumen = async () => {
    setLoadingResumen(true);
    try {
      const r = await lecheApi.resumen(animalId);
      setResumen({
        promedio7Dias: Number(r?.promedio7Dias ?? 0),
        promedio30Dias: Number(r?.promedio30Dias ?? 0),
      });
    } catch (err) {
      // No bloquea la pantalla si falla resumen
      message.error(normalizeErrorMessage(err));
    } finally {
      setLoadingResumen(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadListado(), loadResumen()]);
  };

  useEffect(() => {
    if (!Number.isFinite(animalId) || animalId <= 0) return;
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalId]);

  const abrirNuevo = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      fecha: dayjs(), // hoy
      litrosTotal: 0,
      observacion: "",
    });
    setModalOpen(true);
  };

  const abrirEditar = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      fecha: dayjs(record.fecha),
      litrosTotal: Number(record.litrosTotal ?? 0),
      observacion: record.observacion ?? "",
    });
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onGuardar = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payloadBase = {
        // backend espera DateTime, pero nosotros enviamos "YYYY-MM-DD" para evitar timezone
        fecha: dayjs(values.fecha).format(dateFormat),
        litrosTotal: Number(values.litrosTotal ?? 0),
        observacion: values.observacion ?? null,
      };

      if (!editing) {
        const payload = {
          animalId,
          ...payloadBase,
        };
        await lecheApi.crear(payload);
        message.success("Registro creado correctamente.");
      } else {
        await lecheApi.editar(editing.id, payloadBase);
        message.success("Registro actualizado correctamente.");
      }

      cerrarModal();
      await refreshAll();
    } catch (err) {
      // Si es error de validación del form, no hacemos message.error aquí
      if (err?.errorFields) return;

      message.error(normalizeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onEliminar = async (record) => {
    try {
      await lecheApi.eliminar(record.id);
      message.success("Registro eliminado.");
      await refreshAll();
    } catch (err) {
      message.error(normalizeErrorMessage(err));
    }
  };

  const dataFiltrada = useMemo(() => {
    if (!filtroRango || filtroRango.length !== 2) return data;

    const [ini, fin] = filtroRango;
    const iniDate = dayjs(ini).startOf("day");
    const finDate = dayjs(fin).endOf("day");

    return (data || []).filter((x) => {
      const f = dayjs(x.fecha);
      return (f.isAfter(iniDate) || f.isSame(iniDate)) && (f.isBefore(finDate) || f.isSame(finDate));
    });
  }, [data, filtroRango]);

  const columns = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      render: (v) => dayjs(v).format(dateFormat),
      sorter: (a, b) => dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf(),
      defaultSortOrder: "descend",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Litros Total",
      dataIndex: "litrosTotal",
      key: "litrosTotal",
      align: "right",
      render: (v) => Number(v ?? 0).toFixed(2),
      sorter: (a, b) => Number(a.litrosTotal ?? 0) - Number(b.litrosTotal ?? 0),
    },
    {
      title: "Observación",
      dataIndex: "observacion",
      key: "observacion",
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => abrirEditar(record)}>
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar registro?"
            description="Esta acción no se puede deshacer."
            okText="Sí, eliminar"
            cancelText="Cancelar"
            onConfirm={() => onEliminar(record)}
          >
            <Button size="small" danger>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={3} style={{ margin: 0 }}>
            Historial de Leche
          </Title>
          <Text type="secondary">Animal ID: {animalId}</Text>
        </Col>
        <Col>
          <Space>
            <Button onClick={() => navigate(-1)}>Volver</Button>
            <Button type="primary" onClick={abrirNuevo}>
              Nuevo registro
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card loading={loadingResumen} title="Promedios">
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text type="secondary">Promedio 7 días</Text>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {Number(resumen.promedio7Dias ?? 0).toFixed(2)}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Promedio 30 días</Text>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {Number(resumen.promedio30Dias ?? 0).toFixed(2)}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Filtros">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text type="secondary">Rango de fechas</Text>
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                value={filtroRango}
                onChange={(val) => setFiltroRango(val)}
                format={dateFormat}
                allowClear
              />
              <Button onClick={() => setFiltroRango(null)}>Limpiar filtro</Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }} title="Registros diarios">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataFiltrada}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={tituloModal}
        open={modalOpen}
        onCancel={cerrarModal}
        onOk={onGuardar}
        okText={editing ? "Guardar cambios" : "Crear"}
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Fecha"
            name="fecha"
            rules={[{ required: true, message: "La fecha es obligatoria." }]}
          >
            <DatePicker style={{ width: "100%" }} format={dateFormat} />
          </Form.Item>

          <Form.Item
            label="Litros Total"
            name="litrosTotal"
            rules={[
              { required: true, message: "Los litros son obligatorios." },
              {
                validator: (_, value) => {
                  const n = Number(value);
                  if (Number.isNaN(n)) return Promise.reject(new Error("Valor inválido."));
                  if (n < 0) return Promise.reject(new Error("No puede ser negativo."));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={0.1}
              placeholder="Ej: 8.5"
            />
          </Form.Item>

          <Form.Item label="Observación" name="observacion">
            <Input.TextArea rows={4} maxLength={1000} placeholder="Opcional" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
